export const features = Object.freeze({
  /** Soft-launch feature flags below: */
  /** Feature is disabled for BigCommerce as we sync customer groups via their own platform */
  CUSTOMER_GROUPS_AMEND: 'CUSTOMER_GROUPS_AMEND',
  CUSTOM_MAIL_DOMAIN: 'CUSTOM_MAIL_DOMAIN',
  TEAM_MEMBER_ROLE: 'TEAM_MEMBER_ROLE',
  MULTI_STOCK_LOCATION_SUPPORT: 'MULTI_STOCK_LOCATION_SUPPORT',
  DISCOUNTS: 'DISCOUNTS',
  DISCOUNTS_REQUIREMENT_USAGE_LIMITS: 'DISCOUNTS_REQUIREMENT_USAGE_LIMITS',
  DISCOUNTS_EXCLUDED_CUSTOMERS: 'DISCOUNTS_EXCLUDED_CUSTOMERS',
  DISCOUNTS_COMPATIBILITY: 'DISCOUNTS_COMPATIBILITY',
  DISCOUNTS_FREE_PRODUCT: 'DISCOUNTS_FREE_PRODUCT',
  DISCOUNTS_SHIPPING_REWARD: 'DISCOUNTS_SHIPPING_REWARD',
  DISCOUNTS_PRODUCT_LEVEL_AMOUNT_OFF: 'DISCOUNTS_PRODUCT_LEVEL_AMOUNT_OFF',
  DISCOUNTS_ORDER_RULES: 'DISCOUNTS_ORDER_RULES',
  DISCOUNTS_ADVANCED: 'DISCOUNTS_ADVANCED',
  STOCK_DISPLAY: 'STOCK_DISPLAY',
  THEMES: 'THEMES',
  QUOTING: 'QUOTING',
  INVOICING: 'INVOICING',
  ACCOUNTING: 'ACCOUNTING',
  API_ACCESS: 'API_ACCESS',
  XERO: 'XERO',
  QUICKBOOKS: 'QUICKBOOKS',
  CUSTOMERS_V2: 'CUSTOMERS_V2',
  TEAMS_V2: 'TEAMS_V2',
  COMPANIES: 'COMPANIES',
  PAYMENTS_VISIBILITY: 'PAYMENTS_VISIBILITY',
  ANALYTICS_NAV: 'ANALYTICS_NAV',
  ANALYTICS: 'ANALYTICS',
  SHIPPING: 'SHIPPING',
  RECURRING_ORDERS: 'RECURRING_ORDERS',
});

export type FeatureFlag = keyof typeof features;

const platforms = Object.freeze({
  SHOPIFY: 'shopify',
  SHOPIFY_PLUS: 'shopify_plus',
  BIGCOMMERCE: 'bigcommerce',
  WIX: 'wix',
  WOOCOMMERCE: 'woocommerce',
  MAGENTO: 'magento',
  CUSTOM: 'custom',
});

export type Platform = (typeof platforms)[keyof typeof platforms];

const plans = [
  'basic_free',
  'v2_basic',
  'v2_starter_plus',
  'v2_growth',
  'v2_pro',
  'enterprise',
  'enterprise_onboarding',
  'custom',
] as const;

export type Plan = (typeof plans)[number];

type Feature = {
  beta?: boolean;
  enabledForProduction?: boolean;
  siteIdOverrides?: string[];
  disabledPlatforms?: Platform[];
  requiredPlan?: Plan;
};

const featureState = Object.freeze({
  [features.CUSTOMER_GROUPS_AMEND]: {
    disabledPlatforms: [platforms.BIGCOMMERCE],
  },
  // Hide team member invite role - currently does not do anything
  [features.TEAM_MEMBER_ROLE]: {
    enabledForProduction: false,
  },
  [features.MULTI_STOCK_LOCATION_SUPPORT]: {
    enabledForProduction: true,
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS]: {
    requiredPlan: 'v2_basic',
  },
  [features.DISCOUNTS_REQUIREMENT_USAGE_LIMITS]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_EXCLUDED_CUSTOMERS]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_COMPATIBILITY]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_FREE_PRODUCT]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_PRODUCT_LEVEL_AMOUNT_OFF]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_SHIPPING_REWARD]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_ADVANCED]: {
    requiredPlan: 'v2_pro',
  },
  [features.DISCOUNTS_ORDER_RULES]: {
    requiredPlan: 'v2_growth',
  },
  [features.API_ACCESS]: {
    requiredPlan: 'v2_pro',
  },
  [features.THEMES]: {
    requiredPlan: 'v2_pro',
  },
  [features.QUOTING]: {
    requiredPlan: 'v2_pro',
  },
  [features.INVOICING]: {
    requiredPlan: 'v2_growth',
  },
  [features.XERO]: {
    enabledForProduction: true,
    requiredPlan: 'v2_pro',
    disabledPlatforms: [
      platforms.BIGCOMMERCE,
      platforms.MAGENTO,
      platforms.WIX,
      platforms.WOOCOMMERCE,
    ],
  },
  [features.QUICKBOOKS]: {
    enabledForProduction: true,
    requiredPlan: 'v2_pro',
    disabledPlatforms: [platforms.BIGCOMMERCE, platforms.MAGENTO, platforms.WOOCOMMERCE],
  },
  [features.ACCOUNTING]: {
    enabledForProduction: true,
    disabledPlatforms: [platforms.BIGCOMMERCE, platforms.MAGENTO, platforms.WOOCOMMERCE],
  },
  [features.CUSTOMERS_V2]: {
    enabledForProduction: true,
    disabledPlatforms: [platforms.BIGCOMMERCE, platforms.WOOCOMMERCE],
    siteIdOverrides: ['jamescustomersv2qa', 'stephensfakebcstore'],
  },
  [features.TEAMS_V2]: {
    enabledForProduction: false,
  },
  [features.COMPANIES]: {
    enabledForProduction: false,
  },
  [features.PAYMENTS_VISIBILITY]: {
    enabledForProduction: true,
    disabledPlatforms: [
      platforms.BIGCOMMERCE,
      platforms.MAGENTO,
      platforms.WIX,
      platforms.WOOCOMMERCE,
    ],
  },
  [features.CUSTOM_MAIL_DOMAIN]: {
    requiredPlan: 'v2_pro',
  },
  [features.ANALYTICS_NAV]: {
    enabledForProduction: false,
  },
  [features.ANALYTICS]: {
    requiredPlan: 'v2_pro',
  },
  [features.SHIPPING]: {
    requiredPlan: 'v2_basic',
  },
  [features.RECURRING_ORDERS]: {
    enabledForProduction: false,
    requiredPlan: 'v2_growth',
  },
}) as Readonly<Record<FeatureFlag, Feature>>;

export function featureEnabled(featureFlag: FeatureFlag, siteId: string, platform?: Platform) {
  const feature = featureState[featureFlag];
  const isProd = window.location.host === 'app.sparklayer.io';

  if (feature?.siteIdOverrides?.includes(siteId)) {
    return true;
  }

  if (isProd && !feature?.enabledForProduction && feature?.siteIdOverrides) {
    return false;
  }

  if (platform && feature?.disabledPlatforms?.includes(platform)) {
    return false;
  }

  if (feature?.enabledForProduction === false && isProd) {
    return false;
  }

  return true;
}

export function featureRestricted(featureFlag: FeatureFlag, siteBillingPlan: Plan) {
  const feature = featureState[featureFlag];

  if (feature?.requiredPlan) {
    // if the feature requires a plan, check if we even have a billing plan
    if (!siteBillingPlan) {
      return true;
    }

    const requiredPlan = plans.indexOf(feature.requiredPlan);
    const currentPlan = plans.indexOf(siteBillingPlan);

    if (requiredPlan === -1) {
      return false;
    }

    if (currentPlan === -1) {
      return false;
    }

    return requiredPlan > currentPlan;
  }

  return false;
}
